import { Guard } from "lib/actions/util";
import { viewsToolSet } from "lib/types/views/ViewToolsStateKey";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useRouteParamNumber } from "../../hooks/useRouteParam";
import { useSyncCesium } from "components/elements/CesiumViewer/core/useSyncCesium";
import { useAssets } from "lib/hooks/assets/useAssets";
import { Matrix4, Math as CesiumMath } from "cesium";
import { ViewModel } from "lib/types/views/ViewModel";
import { AssetVersionWithAssetModel } from "lib/types/assets/AssetVersionWithAssetModel";
import { ViewToolsState } from "lib/types/views/ViewToolsState";
import { AssetState } from "components/elements/CesiumViewer/core/wrapper/AssetsPlugin";
import {
  resolveViewerTab,
  ViewerTab,
} from "lib/actions/views/resolveViewerTab";

export type BeforeViewModeState = {
  cameraTransform: number[] | undefined;
  toolsState: ViewToolsState;
  visibleLayerIds: number[];
  visibleContentUris: string[];
};

const ViewsModeContext = createContext<ReturnType<typeof useProvider>>(null);

export const useViewsMode = () => useContext(ViewsModeContext);

export function ViewsModeProvider(p: PropsWithChildren) {
  const value = useProvider();
  return <ViewsModeContext value={value}>{p.children}</ViewsModeContext>;
}

function useProvider() {
  const [loadedView, setLoadedView] = useState<ViewModel | null>(null);
  const [beforeViewModeState, setBeforeViewModeState] =
    useState<BeforeViewModeState | null>(null);
  const [hasCameraMoved, setCameraMoved] = useState(false);
  const [lastPickedTab, setLastPickedTab] = useState<ViewerTab>("snapshot");
  const [isEditingAnnotations, setIsEditingAnnotations] = useState(false);

  const [projectId] = useRouteParamNumber("projectId");
  const { viewer, assetsPlugin, toolManager } = useCesium();
  const assets = useSyncCesium(assetsPlugin.assets);
  const { versions } = useAssets(projectId);

  const hasAssetsChanged =
    loadedView &&
    versions &&
    !visibleAssetsMatchView(loadedView, versions, assets);
  const hasToolsChanged =
    !!loadedView?.toolsStateJson &&
    viewsToolSet
      .values()
      .some(
        (tool) =>
          !toolManager.isMatchingToolState(
            tool,
            loadedView.toolsStateJson[tool],
          ),
      );
  const hasViewChanged = hasCameraMoved || hasAssetsChanged || hasToolsChanged;
  const activeTab = resolveViewerTab(lastPickedTab, !!loadedView?.snapshotId);

  useEffect(() => {
    setLoadedView(null);
  }, [projectId]);

  useEffect(() => {
    setIsEditingAnnotations(false);
  }, [loadedView?.id]);

  useEffect(() => {
    if (!viewer || !loadedView?.cameraPosition?.length) return;

    const onCameraMoveEnd = () => {
      const currentPosition = Matrix4.clone(viewer.camera.inverseViewMatrix);
      setCameraMoved(
        !currentPosition.equalsEpsilon(
          Matrix4.fromArray(loadedView.cameraPosition),
          CesiumMath.EPSILON4,
        ),
      );
    };

    viewer.camera.moveEnd.addEventListener(onCameraMoveEnd);
    return () => {
      viewer.camera.moveEnd.removeEventListener(onCameraMoveEnd);
    };
  }, [viewer, loadedView]);

  function pickTab(tab: ViewerTab) {
    setLastPickedTab(tab);
    if (tab === "3d") setIsEditingAnnotations(false);
  }

  function startEditingAnnotations() {
    setIsEditingAnnotations(true);
  }

  function stopEditingAnnotations() {
    setIsEditingAnnotations(false);
  }

  function exitViewMode() {
    setLoadedView(null);
    setBeforeViewModeState(null);
    setCameraMoved(false);
  }

  return {
    loadedView,
    setLoadedView,
    beforeViewModeState,
    setBeforeViewModeState,
    hasViewChanged,
    setCameraMoved,
    exitViewMode,
    activeTab,
    pickTab,
    isEditingAnnotations,
    startEditingAnnotations,
    stopEditingAnnotations,
  };
}

function visibleAssetsMatchView(
  view: ViewModel,
  versions: AssetVersionWithAssetModel[],
  assetsMap: Map<string, AssetState>,
): boolean {
  Guard.require(view, "view");
  Guard.require(versions, "versions");
  Guard.require(assetsMap, "assetsMap");

  const viewVersionIds = new Set(view.assetVersionIds ?? []);

  const allVisibleUris = new Set(
    [...assetsMap.values()]
      .filter((a) => a.isShowing || a.layerIds.length > 0)
      .map((a) => a.contentUri),
  );

  const visibleVersionIds = new Set(
    versions
      .filter((v) => v.contentUri && allVisibleUris.has(v.contentUri))
      .map((v) => v.id),
  );

  if (visibleVersionIds.size !== viewVersionIds.size) return false;
  for (const id of visibleVersionIds) {
    if (!viewVersionIds.has(id)) return false;
  }
  return true;
}
